import Navigation from "../utils/Navigation";
import React, {useEffect, useRef} from "react";
import {NewsPhoto, NewsPreviewCard} from "./NewsCard";
import {useLocation, Link} from "react-router-dom";
import NewsContentJson from "./newsContentData.json";
import SeoHelmet from "../utils/seo";
import {DocumentationTable} from "../utils/documentationTable";
import {DoubleList, SingleList, NoStrongList,
    TableConstructor, GalleryDescription, GalleryDescriptionLinks, SilosModelsConstructor,
    ProductListParams} from "../utils/utils";
import {fetchRequestNewsView} from "../../api/news/newsApi"
import {getUUID} from "../utils/uuid";
import {useNews} from "./NewsContext";
import {useTheme} from "../header/Header"
import {EyeOutlined} from "@ant-design/icons";

import "../../css/lightTheme/publicLight.css";

// Динамическая загрузка изображений
const images = require.context('../../images/news', true, /\.(webp|webm)$/);
const getImage = (path) => {

    try {
        return images(`./${path}`);
    } catch (error) {
        console.log(`Изображение ${path} не найдено`)
        return ""
    }
}


export const processNewsData = (data, viewsMap = new Map()) => {
    return data
        .sort((a, b) => {
            const parseDate = (dateStr) => {
                const [day, month, year] = dateStr.split('.').map(Number);
                return new Date(year, month - 1, day);
            };
            return parseDate(b.date) - parseDate(a.date);
        })
        .map((news, i) => ({
            ...news,
            url: `/news/${data.length - i}`,
            image: getImage(news.preview),
            views: viewsMap.get(news.title) || 0
        }));
};

export function NewsPreview() {
    const { newsData, refreshNews } = useNews();
    const location = useLocation();
    const { theme } = useTheme();

    const title = "Новости"
    const description = newsData.slice(0, 5).map(e => e.title).join(" ")

    // Теперь refreshNews мемоизирован и безопасен для использования в зависимостях
    useEffect(() => {
        refreshNews();
    }, [location.pathname, refreshNews]);

    return (
        <>
            <SeoHelmet title={title} description={description}/>
            <Navigation/>

            <div className={`news-preview-wrapper`}>
                <div className={`complexBack ${theme}`}> </div>
                <h2 className={`news-preview-title ${theme}`}>Новости</h2>
                {newsData.map(news => (
                    <NewsPreviewCard
                        key={news.url}
                        news={news}
                    />
                ))}
            </div>
        </>
    );
}

export function News() {
    const location = useLocation();
    const { newsData, refreshNews } = useNews();
    const { theme } = useTheme();

    // Получаем ключ текущего маршрута
    const pathNames = location.pathname.split("/").filter((x) => x);
    const currentKey = pathNames[1] || (newsData.length > 0 ? newsData.length - 1 : 0);

    // Защита от пустого newsData
    const currentNews = newsData.length > 0
        ? (newsData[newsData.length - currentKey] || newsData[0])
        : null;

    const currentContent = currentNews ? NewsContentJson[currentNews.title] : null;

    // Используем useRef для отслеживания, была ли уже обработана эта новость
    const processedNewsRef = useRef(new Set());

    useEffect(() => {
        // Если currentNews еще не загружен, не выполняем запрос
        if (!currentNews) return;

        // Если эту новость уже обрабатывали в этой сессии, не выполняем запрос снова
        const newsId = `${currentNews.title}-${location.pathname}`;
        if (processedNewsRef.current.has(newsId)) {
            return;
        }

        // Помечаем новость как обработанную
        processedNewsRef.current.add(newsId);

        async function addViewAndRefresh(){
            try {
                const UUID = getUUID();
                // 1. Сначала отправляем запрос на добавление просмотра
                await fetchRequestNewsView(currentNews.title, UUID);

                // 2. Независимо от результата, обновляем все данные через refreshNews
                await refreshNews();

            } catch (error) {
                console.log("error", error);
                // Даже при ошибке пытаемся обновить данные
                try {
                    await refreshNews();
                } catch (refreshError) {
                    console.log("refresh error", refreshError);
                }
            }
        }

        addViewAndRefresh();
        window.scrollTo(0, 0);
    }, [currentNews, refreshNews, location.pathname]); // Добавляем location.pathname в зависимости

    // Если новость не найдена
    if (!currentNews) {
        return (
            <>
                <SeoHelmet title="Новость не найдена" description="Запрошенная новость не существует"/>
                <Navigation/>
                <div className="complexBack"> </div>
                <div className={`news-card-description ${theme}`}>
                    <div>Новость не найдена</div>
                </div>
            </>
        );
    }

    return (
        <>
            <SeoHelmet title={currentNews.title} description={currentNews.previewText}/>
            <div className={`complexBack ${theme}`}> </div>
            <div className={`news-card-description ${theme}`}>
                <Navigation/>
                <div className="news-meta-container">
                    <i className={`news-date ${theme}`}>{currentNews.date}</i>
                    <div className="news-views">
                        <EyeOutlined className="eye-icon" />
                        <span className={`views-count ${theme}`}>{currentNews.views || 0}</span>
                    </div>
                </div>
                <h3>{currentNews.title}</h3>
                {!currentContent || currentContent.images.length === 0? null : <NewsPhoto images={currentContent.images.map(e=>getImage(e))}/>}
                <div className={"news-content"}>
                    <p>{currentNews.previewText}</p>
                    {currentContent && currentContent.content? currentContent.content.map((e, i) => {
                        return (
                            <React.Fragment key={`content${i}`}>
                                {htmlConverter(e)}
                            </React.Fragment>
                        )
                    }) : null}
                </div>
            </div>
        </>
    );
}

export function htmlConverter(object) {

    const key = Object.keys(object)[0]
    const content = object[key]

    switch (key) {
        case "p":
            return <p>{content}</p>
        case "a":
            return <p>{content.text}{<Link to={content.to}> {content.linkText}</Link>}</p>
        case "h1":
            return <h1>{content}</h1>
        case "h2":
            return <h2>{content}</h2>
        case "h3":
            return <h3>{content}</h3>
        case "strong":
            return <strong>{content}</strong>
        case "img":
            return <img src={getImage(content)} alt="no-img"/>
        case "ul":
            return <ul>
                {content.map(li=>
                    <li key={li}>{li}</li>
                )}
            </ul>
        case "productParams":
            return <ProductListParams params={content}/>
        case "singleList":
            return <SingleList list={content}/>
        case "doubleList":
            return <DoubleList list={content}/>
        case "numberList":
            return <NoStrongList list={content} numbered={true}/>
        case "table":
            return <TableConstructor data={content}/>
        case "silosModels":
            return <SilosModelsConstructor data={content}/>
        case "galleryList":
            return <GalleryDescription data={content}/>
        case "galleryLinks":
            return <GalleryDescriptionLinks data={content}/>
        case "doc":
            return <DocumentationTable documents={content}/>

        default:
            return <>{content}</>
    }
}